import type { HubResource, HubSection } from '../types/hub';
import { toolkitPath } from './placePaths';

export const HUB_PREVIEW_COUNT = 3;

export function programHubHref(programSlug: string, hubSlug: string): string {
  return `/${programSlug}/${hubSlug}`;
}

/** A toolkit inside a subject is a section of that subject. A toolkit with no subject stands alone. */
export function hubPlaceHref(hub: { slug: string; program?: { slug: string } | null }): string {
  return toolkitPath(hub);
}

export function orderedByOrder<T extends { order: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.order - b.order);
}

export function previewResources(resources: HubResource[]): HubResource[] {
  const ordered = orderedByOrder(resources);
  const highlighted = ordered.filter((resource) => resource.isHighlighted);
  return highlighted.length > 0 ? highlighted : ordered.slice(0, HUB_PREVIEW_COUNT);
}

export interface HubOutlineEntry {
  id: string;
  title: string;
  section: HubSection | null;
  resources: HubResource[];
}

export interface HubOutlineNode {
  id: string;
  label: string;
  entry: HubOutlineEntry | null;
  children: HubOutlineNode[];
}

function outlineGroupId(path: string) {
  const slug = path
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `group-${slug || 'section'}`;
}

/** Group slash titles so a parent name can appear once, with its children nested. */
export function hubOutlineTree(entries: HubOutlineEntry[]): HubOutlineNode[] {
  const roots: HubOutlineNode[] = [];
  const byPath = new Map<string, HubOutlineNode>();

  const ensure = (segments: string[]): HubOutlineNode => {
    const path = segments.join(' / ');
    const found = byPath.get(path);
    if (found) return found;
    const node: HubOutlineNode = {
      id: outlineGroupId(path),
      label: segments[segments.length - 1] ?? path,
      entry: null,
      children: [],
    };
    byPath.set(path, node);
    if (segments.length === 1) roots.push(node);
    else ensure(segments.slice(0, -1)).children.push(node);
    return node;
  };

  for (const entry of entries) {
    if (entry.id === 'more') continue;
    const segments = entry.title.split(' / ').map((part) => part.trim()).filter(Boolean);
    if (segments.length === 0) continue;
    const node = ensure(segments);
    node.entry = entry;
    node.id = entry.id;
    node.label = segments[segments.length - 1] ?? entry.title;
  }

  const more = entries.find((entry) => entry.id === 'more');
  if (more) roots.push({ id: more.id, label: more.title, entry: more, children: [] });
  return roots;
}

export function outlineBranchVisible(node: HubOutlineNode, filtering: boolean): boolean {
  const section = node.entry?.section;
  const own =
    (node.entry?.resources.length ?? 0) > 0 ||
    Boolean(section?.type === 'CONTENT' && section.body);
  const child = node.children.some((item) => outlineBranchVisible(item, filtering));
  if (own || child) return true;
  return !filtering && Boolean(node.entry);
}

/** Sections worth listing under a hub. A single section is the hub's resource list. */
export function hubOutline(sections: HubSection[], resources: HubResource[]): HubOutlineEntry[] {
  const orderedSections = orderedByOrder(sections);
  if (orderedSections.length <= 1) return [];

  const entries: HubOutlineEntry[] = orderedSections.map((section) => ({
    id: section.id,
    title: section.title,
    section,
    resources: orderedByOrder(resources.filter((resource) => resource.sectionId === section.id)),
  }));

  const ungrouped = orderedByOrder(resources.filter((resource) => !resource.sectionId));
  if (ungrouped.length > 0) {
    entries.push({
      id: 'more',
      title: 'More resources',
      section: null,
      resources: ungrouped,
    });
  }

  return entries;
}

export function parseProgramFocus(
  hash: string,
  hubs: Array<{ slug: string; sections: HubSection[]; resources: HubResource[] }>
): { hubSlug: string | null; sectionId: string | null } {
  const id = hash.replace(/^#/, '');
  if (!id) return { hubSlug: null, sectionId: null };

  if (id.startsWith('hub-section-')) {
    const sectionKey = id.slice('hub-section-'.length);
    for (const hub of hubs) {
      const entry = hubOutline(hub.sections, hub.resources).find((item) => item.id === sectionKey);
      if (entry) return { hubSlug: hub.slug, sectionId: entry.id };
    }
    return { hubSlug: null, sectionId: null };
  }

  if (id.startsWith('hub-')) {
    const slug = id.slice('hub-'.length);
    const hub = hubs.find((item) => item.slug === slug);
    if (!hub) return { hubSlug: null, sectionId: null };
    const outline = hubOutline(hub.sections, hub.resources);
    return { hubSlug: slug, sectionId: outline[0]?.id ?? null };
  }

  return { hubSlug: null, sectionId: null };
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'start',
  });
  window.dispatchEvent(new Event('scroll'));
}

const SCROLL_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'PageDown',
  'PageUp',
  'Home',
  'End',
  ' ',
]);

export function withSmoothScroll(state: unknown) {
  const prior = state && typeof state === 'object' ? { ...(state as Record<string, unknown>) } : {};
  prior.scrollBehavior = 'smooth';
  return prior;
}

export function hashScrollBehavior(state: unknown): ScrollBehavior {
  if (state && typeof state === 'object' && (state as { scrollBehavior?: string }).scrollBehavior === 'smooth') {
    return 'smooth';
  }
  return 'auto';
}

/**
 * Scroll a hash target under the header, then keep it there while content
 * above it loads and changes the page height. Lets go once the user scrolls,
 * or once the page has been still for a moment.
 * Sidebar clicks pass smooth. Opening a section from another page stays instant
 * so the heading can be held while documents load.
 */
export function pinScrollAnchor(id: string, behavior: ScrollBehavior = 'auto'): () => void {
  const el = document.getElementById(id);
  if (!el) return () => {};

  const motion = prefersReducedMotion() ? 'auto' : behavior;
  el.scrollIntoView({ behavior: motion, block: 'start' });

  if (motion === 'auto') {
    const releasePin = holdAnchor(id);
    return releasePin;
  }

  let cancelled = false;
  let releasePin = () => {};
  const listenerOptions = { passive: true } as const;

  const cancelForUser = () => {
    cancelled = true;
  };

  const onSmoothKey = (event: KeyboardEvent) => {
    if (SCROLL_KEYS.has(event.key)) cancelForUser();
  };

  const detachSmoothListeners = () => {
    window.removeEventListener('wheel', cancelForUser);
    window.removeEventListener('touchmove', cancelForUser);
    window.removeEventListener('keydown', onSmoothKey);
  };

  window.addEventListener('wheel', cancelForUser, listenerOptions);
  window.addEventListener('touchmove', cancelForUser, listenerOptions);
  window.addEventListener('keydown', onSmoothKey);

  let lastY = window.scrollY;
  let startY = lastY;
  let seenMove = false;
  let stableFrames = 0;
  let frames = 0;
  let passes = 0;

  const settle = () => {
    if (cancelled) {
      detachSmoothListeners();
      return;
    }
    const node = document.getElementById(id);
    const desired = node ? parseFloat(getComputedStyle(node).scrollMarginTop) || 0 : 0;
    const top = node ? node.getBoundingClientRect().top : desired;
    if (node && passes < 1 && Math.abs(top - desired) > 12) {
      passes += 1;
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      seenMove = false;
      stableFrames = 0;
      frames = 0;
      lastY = window.scrollY;
      startY = lastY;
      requestAnimationFrame(watch);
      return;
    }
    detachSmoothListeners();
    releasePin = holdAnchor(id);
  };

  const watch = () => {
    if (cancelled) {
      detachSmoothListeners();
      return;
    }
    frames += 1;
    const y = window.scrollY;
    if (Math.abs(y - startY) > 2) seenMove = true;
    if (!seenMove) {
      if (frames > 30) settle();
      else requestAnimationFrame(watch);
      return;
    }
    if (Math.abs(y - lastY) < 1) stableFrames += 1;
    else stableFrames = 0;
    lastY = y;
    if (stableFrames >= 5) settle();
    else requestAnimationFrame(watch);
  };

  requestAnimationFrame(watch);

  return () => {
    cancelled = true;
    detachSmoothListeners();
    releasePin();
  };
}

function holdAnchor(id: string): () => void {
  const el = document.getElementById(id);
  if (!el) return () => {};

  const targetTop = el.getBoundingClientRect().top;
  let expectedY = window.scrollY;
  let released = false;
  let quietTimer = 0;
  let queued = false;
  let adjusting = false;

  const root = document.documentElement;
  const previousAnchor = root.style.overflowAnchor;
  root.style.overflowAnchor = 'none';

  let observer: ResizeObserver | null = null;
  let maxTimer = 0;

  const release = () => {
    if (released) return;
    released = true;
    window.clearTimeout(quietTimer);
    window.clearTimeout(maxTimer);
    observer?.disconnect();
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('keydown', onKey);
    root.style.overflowAnchor = previousAnchor;
  };

  const armQuietTimer = () => {
    window.clearTimeout(quietTimer);
    quietTimer = window.setTimeout(release, 2500);
  };

  const correct = () => {
    if (released) return;
    const node = document.getElementById(id);
    if (!node) return;
    const delta = node.getBoundingClientRect().top - targetTop;
    if (Math.abs(delta) < 1) return;
    adjusting = true;
    expectedY = window.scrollY + delta;
    window.scrollTo({ top: expectedY, behavior: 'auto' });
    expectedY = window.scrollY;
    adjusting = false;
    armQuietTimer();
  };

  const onScroll = () => {
    if (released || adjusting) return;
    if (Math.abs(window.scrollY - expectedY) > 8) release();
  };

  const onKey = (event: KeyboardEvent) => {
    if (SCROLL_KEYS.has(event.key)) release();
  };

  const listenerOptions = { passive: true } as const;
  observer = new ResizeObserver(() => {
    if (released || queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      correct();
    });
  });
  maxTimer = window.setTimeout(release, 20000);
  observer.observe(root);
  window.addEventListener('scroll', onScroll, listenerOptions);
  window.addEventListener('keydown', onKey);

  return release;
}
